// SPDX-License-Identifier: MIT
//
// 2026-05-19 Phase X.A — User-provided AI provider credentials (BYO API key).
// Parallel to the Phase 3.A BYO Supabase backend pattern : the user pastes
// their Anthropic API key once + Genesis stores it RLS-gated in
// public.user_ai_providers. Every subsequent Magnify / Discover call reads
// the user's key via service_role inside the edge fn — the SPA only ever
// POSTs the key on save, never sees it back.
//
// Why a separate module : the API surface is narrow (3 fns), the table is
// user-global (not per-project), and `src/projects/api.ts` is already
// busy with project-row helpers. Keeping the AI provider concerns
// isolated makes the future "multi-provider (gemini, openai)" expansion
// easier to navigate.

import { supabase } from './supabase';
// Canonical provider-kind union lives in the dependency-free provider-config
// module (so the desktop bridge can use it without supabase-js). Re-exported here
// for the existing web consumers. Web wires only 'anthropic' today ; 'sovereign'
// is desktop-first (Point A), 'gemini'/'openai' remain placeholders.
import type { AiProviderKind } from './provider-config';
export type { AiProviderKind };

export interface UserAiProvider {
  user_id: string;
  provider_kind: AiProviderKind;
  api_key: string;
  created_at: string;
  updated_at: string;
}

// Returns the current user's AI provider row, or null when none is set
// (newbie state, pre-"Connect AI" step). RLS gates this to own row only.
export async function getUserAiProvider(): Promise<UserAiProvider | null> {
  const { data, error } = await supabase
    .from('user_ai_providers')
    .select('user_id, provider_kind, api_key, created_at, updated_at')
    .maybeSingle();
  if (error) {
    console.warn('[ai-provider] getUserAiProvider failed', error);
    return null;
  }
  return (data as UserAiProvider | null) ?? null;
}

// Upserts the user's API key. Single provider per user in this Phase X.A
// (user-global, not per-project). The upsert uses user_id as conflict key
// — re-saving rotates the stored key + bumps updated_at via the DB trigger.
// Client-side validation mirrors the DB CHECK : Anthropic keys start with
// `sk-ant-` and are ≥ 80 chars.
export async function saveUserAiProvider(apiKey: string): Promise<UserAiProvider> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('not_authenticated');

  const { data, error } = await supabase
    .from('user_ai_providers')
    .upsert({
      user_id: user.id,
      provider_kind: 'anthropic',
      api_key: apiKey,
    })
    .select()
    .single();
  if (error) throw error;
  return data as UserAiProvider;
}

// Clears the user's stored key. Magnify / Discover then fall back to the
// container env ANTHROPIC_API_KEY (legacy) OR return 403 ai_not_configured
// when that's not set either (production with strict BYO).
export async function clearUserAiProvider(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('not_authenticated');

  const { error } = await supabase
    .from('user_ai_providers')
    .delete()
    .eq('user_id', user.id);
  if (error) throw error;
}

// Mask helper for UI display. We never want the full key on screen even
// for the legitimate owner — first 10 chars + "…" + last 4 is enough to
// confirm "yes that's mine" without making screenshots / screen-shares
// dangerous. The full key only exists in the input field at save time
// and in the encrypted-at-rest DB row.
export function maskAnthropicKey(key: string): string {
  if (key.length < 16) return '…';
  return `${key.slice(0, 10)}…${key.slice(-4)}`;
}
